import React, { useMemo } from 'react';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  CpuChipIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Badge, Button, Label, Spinner, Switch, Tabs, TabsContent, TabsList, TabsTrigger } from '../ui';
import type { StructuredAnalyticsDraft } from './structuredAnalyticsConfig';
import type {
  SemanticContextMCPPolicyResponse,
  SemanticContextMCPReadinessCheck,
  SemanticContextMCPReadinessResponse,
  SemanticContextMCPToolExposure,
} from '../../hooks/useWorkspaceSemanticContext';
import { cn } from '../../shared/lib/cn';

export interface WorkspaceMCPToolsSectionProps {
  draft: StructuredAnalyticsDraft;
  workspaceId: number;
  mcpEnabled: boolean;
  mcpAdminAllowed: boolean;
  mcpPolicy?: SemanticContextMCPPolicyResponse;
  mcpReadiness?: SemanticContextMCPReadinessResponse;
  mcpToolSupport?: Record<string, boolean>;
  canManageMcpPolicy?: boolean;
  canUpdateWorkspace?: boolean;
  onToggleMcp: (enabled: boolean) => void;
  isUpdating?: boolean;
  isPolicyLoading?: boolean;
  isReadinessLoading?: boolean;
}

const STATUS_LABELS: Record<SemanticContextMCPReadinessResponse['effective_status'], string> = {
  ready_for_external_mcp: 'Ready for external MCP',
  blocked_by_platform_route: 'Blocked by platform route',
  blocked_by_tenant_policy: 'Blocked by tenant policy',
  blocked_by_workspace_policy: 'Blocked by workspace policy',
  blocked_by_workspace_opt_in: 'Blocked by workspace opt-in',
  blocked_by_user_permission: 'Blocked by user permission',
  blocked_by_tool_policy: 'Blocked by tool policy',
  blocked_by_workspace_capability: 'Blocked by workspace capability',
  blocked_by_active_pack: 'Blocked by active pack',
  blocked_by_oauth_redirect_uri: 'Blocked by OAuth redirect URI',
};

const ADMIN_POLICY_TARGET = '/admin/mcp-servers';

export function WorkspaceMCPToolsSection({
  draft,
  workspaceId,
  mcpEnabled,
  mcpAdminAllowed,
  mcpPolicy,
  mcpReadiness,
  mcpToolSupport,
  canManageMcpPolicy = false,
  canUpdateWorkspace = true,
  onToggleMcp,
  isUpdating = false,
  isPolicyLoading = false,
  isReadinessLoading = false,
}: WorkspaceMCPToolsSectionProps) {
  const isLoading = isReadinessLoading || (!mcpReadiness && isPolicyLoading);
  const readiness = mcpReadiness;
  const oauth = readiness?.oauth ?? mcpPolicy?.oauth;
  const tools = readiness?.tools ?? mcpPolicy?.tools ?? [];
  const firstBlocker = readiness?.first_blocker;
  const tenantAllowed = readiness
    ? readiness.tenant_enabled && readiness.workspace_allowed
    : mcpPolicy
      ? mcpPolicy.tenant_enabled && mcpPolicy.workspace_allowed
      : mcpAdminAllowed;
  const effectiveEnabled = readiness
    ? readiness.status === 'ready'
    : mcpPolicy
      ? mcpPolicy.effective_enabled
      : mcpEnabled && mcpAdminAllowed;
  const activePackLabel = readiness?.active_context_pack?.version
    ? `pack-v${readiness.active_context_pack.version}`
    : draft.activeRuntimePackVersion ?? 'No active pack';
  const canToggleWorkspaceOptIn =
    canUpdateWorkspace && tenantAllowed && !isUpdating && !isLoading;
  const canOpenExplorer = effectiveEnabled && !isUpdating && !isLoading;
  const adminRequestText = buildAdminRequest(workspaceId, readiness);
  const statusTone = readiness?.status === 'ready' ? 'success' : 'warning';
  const structuredTool = tools.find((tool) => tool.name === 'structured_query');
  const structuredSupported =
    structuredTool?.workspace_capability_supported ?? mcpToolSupport?.structured_query ?? true;
  const scopeLabels = useMemo(() => {
    if (!readiness) return [];
    return [
      formatWorkspaceScope(readiness.allowed_workspace_scope),
      formatToolScope(readiness.allowed_tool_scope),
    ];
  }, [readiness]);

  return (
    <section
      id="mcp-integration"
      className="mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface"
    >
      <div className="flex flex-col gap-4 border-b border-gray-200 px-5 py-5 dark:border-dark-border lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 gap-4">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2">
            <CpuChipIcon className="h-8 w-8 text-eliza-red" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              Model Context Protocol
            </div>
            <h2 className="mt-1 font-subtitle text-h2 leading-tight text-charcoal dark:text-white">
              External AI access
            </h2>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600 dark:text-gray-300">
              Verify whether external MCP clients can see this workspace and call published tools through the shared tenant endpoint.
            </p>
          </div>
        </div>
        <div className="flex flex-col items-start gap-3 sm:items-end">
          <Badge variant="secondary" className="h-fit whitespace-nowrap">
            Workspace {workspaceId}
          </Badge>
          <WorkspaceOptInControl
            mcpEnabled={readiness?.workspace_enabled ?? mcpEnabled}
            disabled={!canToggleWorkspaceOptIn}
            isUpdating={isUpdating || isLoading}
            onToggleMcp={onToggleMcp}
          />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-3 border-b border-gray-200 px-5 py-3 text-sm text-gray-600 dark:border-dark-border dark:text-gray-300">
          <Spinner size="sm" />
          Loading MCP readiness...
        </div>
      )}

      {!readiness && !isLoading && (
        <div className="border-b border-amber-100 bg-amber-50 px-5 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-200">
          MCP readiness is unavailable. The backend readiness endpoint must load before this workspace can be marked ready.
        </div>
      )}

      <div className="space-y-5 px-5 py-5">
        <StatusBanner
          readiness={readiness}
          firstBlocker={firstBlocker}
          statusTone={statusTone}
          adminRequestText={adminRequestText}
          canManageMcpPolicy={canManageMcpPolicy}
          canOpenWorkspaceTarget={canUpdateWorkspace}
        />

        {scopeLabels.length > 0 && (
          <div className="grid gap-2 md:grid-cols-2">
            {scopeLabels.map((label) => (
              <div
                key={label}
                className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300"
              >
                {label}
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="setup">
          <TabsList variant="underline" className="w-full overflow-x-auto">
            <TabsTrigger value="setup" variant="underline">Setup</TabsTrigger>
            <TabsTrigger value="tools" variant="underline">Tools</TabsTrigger>
            <TabsTrigger value="connect" variant="underline">Connect</TabsTrigger>
          </TabsList>

          <TabsContent value="setup" className="space-y-5">
            <SetupJourney
              readiness={readiness}
              checks={readiness?.checks ?? []}
              canManageMcpPolicy={canManageMcpPolicy}
              canUpdateWorkspace={canUpdateWorkspace}
              isUpdating={isUpdating}
              isLoading={isLoading}
              mcpEnabled={readiness?.workspace_enabled ?? mcpEnabled}
              onToggleMcp={onToggleMcp}
              adminRequestText={adminRequestText}
              effectiveStatus={readiness?.effective_status}
            />
            <ReadinessChecklist
              checks={readiness?.checks ?? []}
              canManageMcpPolicy={canManageMcpPolicy}
              canUpdateWorkspace={canUpdateWorkspace}
              isUpdating={isUpdating}
              isLoading={isLoading}
              mcpEnabled={readiness?.workspace_enabled ?? mcpEnabled}
              onToggleMcp={onToggleMcp}
              adminRequestText={adminRequestText}
              effectiveStatus={readiness?.effective_status}
            />
          </TabsContent>

          <TabsContent value="tools">
            <PublishedToolsPanel
              tools={tools}
              structuredSupported={structuredSupported}
            />
          </TabsContent>

          <TabsContent value="connect">
            <ConnectionDetailsPanel
              oauth={oauth}
              activePackLabel={activePackLabel}
              redirectUriCount={readiness?.oauth.redirect_uri_count ?? oauth?.oauth_redirect_uris.length ?? 0}
              smoke={readiness?.smoke}
            />
          </TabsContent>
        </Tabs>
      </div>

      <div className="flex flex-wrap gap-2 border-t border-gray-200 px-5 py-4 dark:border-dark-border">
        <Button type="button" variant="outline" size="sm" onClick={() => window.open('/docs/mcp', '_blank')}>
          <InformationCircleIcon className="mr-2 h-4 w-4" />
          MCP documentation
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => window.open(ADMIN_POLICY_TARGET, '_blank')}>
          <ArrowTopRightOnSquareIcon className="mr-2 h-4 w-4" />
          MCP access policy
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => window.open('/v1/mcp/explorer', '_blank')}
          disabled={!canOpenExplorer}
        >
          {isUpdating ? (
            <ArrowPathIcon className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ArrowTopRightOnSquareIcon className="mr-2 h-4 w-4" />
          )}
          Open MCP Explorer
        </Button>
        {!canOpenExplorer && (
          <span className="self-center text-xs text-gray-500 dark:text-gray-400">
            Explorer opens after backend readiness reports this workspace ready.
          </span>
        )}
      </div>
    </section>
  );
}

function WorkspaceOptInControl({
  mcpEnabled,
  disabled,
  isUpdating,
  onToggleMcp,
}: {
  mcpEnabled: boolean;
  disabled: boolean;
  isUpdating: boolean;
  onToggleMcp: (enabled: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2 dark:border-dark-border dark:bg-dark-surface-2/30">
      <div className="flex flex-col items-end">
        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Workspace opt-in
        </Label>
        <span className={cn('text-xs font-semibold', mcpEnabled ? 'text-green-600 dark:text-green-400' : 'text-gray-500')}>
          {mcpEnabled ? 'ON' : 'OFF'}
        </span>
      </div>
      {isUpdating && <Spinner size="sm" />}
      <Switch
        checked={mcpEnabled}
        onCheckedChange={onToggleMcp}
        disabled={disabled}
        aria-label={mcpEnabled ? 'Disable workspace MCP opt-in' : 'Enable workspace MCP opt-in'}
      />
    </div>
  );
}

function StatusBanner({
  readiness,
  firstBlocker,
  statusTone,
  adminRequestText,
  canManageMcpPolicy,
  canOpenWorkspaceTarget,
}: {
  readiness?: SemanticContextMCPReadinessResponse;
  firstBlocker?: SemanticContextMCPReadinessResponse['first_blocker'];
  statusTone: 'success' | 'warning';
  adminRequestText: string;
  canManageMcpPolicy: boolean;
  canOpenWorkspaceTarget: boolean;
}) {
  const label = readiness ? STATUS_LABELS[readiness.effective_status] : 'Readiness unavailable';
  const bannerClass = statusTone === 'success'
    ? 'border-green-200 bg-green-50 text-green-900 dark:border-green-900/40 dark:bg-green-900/10 dark:text-green-100'
    : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-100';
  const canOpenTarget = canOpenReadinessTarget(firstBlocker?.ui_target, {
    canManageMcpPolicy,
    canOpenWorkspaceTarget,
  });

  return (
    <div className={cn('rounded-lg border p-4', bannerClass)}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {statusTone === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-400" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            )}
            <h3 className="text-base font-semibold">{label}</h3>
          </div>
          <p className="mt-2 text-sm leading-6">
            {firstBlocker?.message ?? 'Internal MCP readiness checks passed. Client OAuth and live tool calls still need to be tested from a real MCP client.'}
          </p>
          {firstBlocker?.next_action && (
            <p className="mt-1 text-xs font-medium">
              Next action: {firstBlocker.next_action}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {firstBlocker?.ui_target && canOpenTarget && (
            <Button type="button" variant="outline" size="sm" onClick={() => window.open(firstBlocker.ui_target ?? ADMIN_POLICY_TARGET, '_blank')}>
              <ArrowTopRightOnSquareIcon className="mr-2 h-4 w-4" />
              Open target
            </Button>
          )}
          {firstBlocker && !canOpenTarget && (
            <CopyButton label="Admin request" value={adminRequestText} />
          )}
        </div>
      </div>
    </div>
  );
}

type SetupStep = {
  key: string;
  title: string;
  description: string;
  status: SemanticContextMCPReadinessCheck['status'];
  check?: SemanticContextMCPReadinessCheck;
};

function SetupJourney({
  readiness,
  checks,
  canManageMcpPolicy,
  canUpdateWorkspace,
  isUpdating,
  isLoading,
  mcpEnabled,
  onToggleMcp,
  adminRequestText,
  effectiveStatus,
}: {
  readiness?: SemanticContextMCPReadinessResponse;
  checks: SemanticContextMCPReadinessCheck[];
  canManageMcpPolicy: boolean;
  canUpdateWorkspace: boolean;
  isUpdating: boolean;
  isLoading: boolean;
  mcpEnabled: boolean;
  onToggleMcp: (enabled: boolean) => void;
  adminRequestText: string;
  effectiveStatus?: SemanticContextMCPReadinessResponse['effective_status'];
}) {
  const steps = buildSetupSteps(readiness, checks);

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2/40">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-medium text-charcoal dark:text-white">MCP setup path</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            These steps show where access is approved, exposed, connected, and verified.
          </p>
        </div>
        <Badge variant="outline">{steps.filter((step) => step.status === 'passed').length} of {steps.length} complete</Badge>
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        {steps.map((step, index) => (
          <SetupStepCard
            key={step.key}
            step={step}
            index={index}
            canManageMcpPolicy={canManageMcpPolicy}
            canUpdateWorkspace={canUpdateWorkspace}
            isUpdating={isUpdating}
            isLoading={isLoading}
            mcpEnabled={mcpEnabled}
            onToggleMcp={onToggleMcp}
            adminRequestText={adminRequestText}
            effectiveStatus={effectiveStatus}
          />
        ))}
      </div>
    </div>
  );
}

function SetupStepCard({
  step,
  index,
  canManageMcpPolicy,
  canUpdateWorkspace,
  isUpdating,
  isLoading,
  mcpEnabled,
  onToggleMcp,
  adminRequestText,
  effectiveStatus,
}: {
  step: SetupStep;
  index: number;
  canManageMcpPolicy: boolean;
  canUpdateWorkspace: boolean;
  isUpdating: boolean;
  isLoading: boolean;
  mcpEnabled: boolean;
  onToggleMcp: (enabled: boolean) => void;
  adminRequestText: string;
  effectiveStatus?: SemanticContextMCPReadinessResponse['effective_status'];
}) {
  const isBlocked = step.status === 'blocked';

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-xs font-semibold text-gray-600 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300">
          {index + 1}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-charcoal dark:text-white">{step.title}</h4>
            <Badge variant={step.status === 'passed' ? 'success' : step.status === 'blocked' ? 'warning' : 'secondary'}>
              {formatStatus(step.status)}
            </Badge>
          </div>
          <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{step.description}</p>
        </div>
      </div>
      {isBlocked && step.check && (
        <div className="mt-3 flex flex-wrap gap-2 pl-10">
          <ReadinessAction
            check={step.check}
            canManageMcpPolicy={canManageMcpPolicy}
            canUpdateWorkspace={canUpdateWorkspace}
            isUpdating={isUpdating}
            isLoading={isLoading}
            mcpEnabled={mcpEnabled}
            onToggleMcp={onToggleMcp}
            adminRequestText={adminRequestText}
            effectiveStatus={effectiveStatus}
          />
        </div>
      )}
    </div>
  );
}

function ReadinessChecklist({
  checks,
  canManageMcpPolicy,
  canUpdateWorkspace,
  isUpdating,
  isLoading,
  mcpEnabled,
  onToggleMcp,
  adminRequestText,
  effectiveStatus,
}: {
  checks: SemanticContextMCPReadinessCheck[];
  canManageMcpPolicy: boolean;
  canUpdateWorkspace: boolean;
  isUpdating: boolean;
  isLoading: boolean;
  mcpEnabled: boolean;
  onToggleMcp: (enabled: boolean) => void;
  adminRequestText: string;
  effectiveStatus?: SemanticContextMCPReadinessResponse['effective_status'];
}) {
  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-charcoal dark:text-white">Readiness checklist</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Ordered by the backend readiness endpoint.
          </p>
        </div>
        <Badge variant="outline">{checks.length} checks</Badge>
      </div>
      <div className="space-y-2">
        {checks.map((check) => (
          <ChecklistRow
            key={check.key}
            check={check}
            canManageMcpPolicy={canManageMcpPolicy}
            canUpdateWorkspace={canUpdateWorkspace}
            isUpdating={isUpdating}
            isLoading={isLoading}
            mcpEnabled={mcpEnabled}
            onToggleMcp={onToggleMcp}
            adminRequestText={adminRequestText}
            effectiveStatus={effectiveStatus}
          />
        ))}
        {checks.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
            Readiness checks have not loaded.
          </div>
        )}
      </div>
    </div>
  );
}

function ChecklistRow({
  check,
  canManageMcpPolicy,
  canUpdateWorkspace,
  isUpdating,
  isLoading,
  mcpEnabled,
  onToggleMcp,
  adminRequestText,
  effectiveStatus,
}: {
  check: SemanticContextMCPReadinessCheck;
  canManageMcpPolicy: boolean;
  canUpdateWorkspace: boolean;
  isUpdating: boolean;
  isLoading: boolean;
  mcpEnabled: boolean;
  onToggleMcp: (enabled: boolean) => void;
  adminRequestText: string;
  effectiveStatus?: SemanticContextMCPReadinessResponse['effective_status'];
}) {
  const isBlocked = check.status === 'blocked';

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface-2/40 lg:flex-row lg:items-start lg:justify-between">
      <div className="flex min-w-0 gap-3">
        <CheckIcon status={check.status} />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-charcoal dark:text-white">{check.label}</h4>
            <Badge variant={check.status === 'passed' ? 'success' : check.status === 'blocked' ? 'warning' : 'secondary'}>
              {formatStatus(check.status)}
            </Badge>
            {check.code && (
              <code className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600 dark:bg-dark-surface dark:text-gray-300">
                {check.code}
              </code>
            )}
          </div>
          <p className="mt-1 text-xs leading-5 text-gray-600 dark:text-gray-300">{check.message}</p>
          {check.next_action && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Next action: {check.next_action}</p>
          )}
        </div>
      </div>
      {isBlocked && (
        <div className="flex flex-shrink-0 flex-wrap gap-2 lg:justify-end">
          <ReadinessAction
            check={check}
            canManageMcpPolicy={canManageMcpPolicy}
            canUpdateWorkspace={canUpdateWorkspace}
            isUpdating={isUpdating}
            isLoading={isLoading}
            mcpEnabled={mcpEnabled}
            onToggleMcp={onToggleMcp}
            adminRequestText={adminRequestText}
            effectiveStatus={effectiveStatus}
          />
        </div>
      )}
    </div>
  );
}

function ReadinessAction({
  check,
  canManageMcpPolicy,
  canUpdateWorkspace,
  isUpdating,
  isLoading,
  mcpEnabled,
  onToggleMcp,
  adminRequestText,
  effectiveStatus,
}: {
  check: SemanticContextMCPReadinessCheck;
  canManageMcpPolicy: boolean;
  canUpdateWorkspace: boolean;
  isUpdating: boolean;
  isLoading: boolean;
  mcpEnabled: boolean;
  onToggleMcp: (enabled: boolean) => void;
  adminRequestText: string;
  effectiveStatus?: SemanticContextMCPReadinessResponse['effective_status'];
}) {
  const canInlineToggle =
    check.key === 'workspace_opt_in'
    && check.status === 'blocked'
    && canUpdateWorkspace
    && effectiveStatus === 'blocked_by_workspace_opt_in';
  const canOpenTarget = canOpenReadinessTarget(check.ui_target, {
    canManageMcpPolicy,
    canOpenWorkspaceTarget: canUpdateWorkspace,
  });

  if (canInlineToggle) {
    return (
      <Button type="button" size="sm" variant="outline" onClick={() => onToggleMcp(!mcpEnabled)} disabled={isUpdating || isLoading}>
        {isUpdating ? <Spinner size="sm" className="mr-2" /> : null}
        Enable workspace opt-in
      </Button>
    );
  }

  if (check.ui_target && canOpenTarget) {
    return (
      <Button type="button" size="sm" variant="outline" onClick={() => window.open(check.ui_target ?? ADMIN_POLICY_TARGET, '_blank')}>
        <ArrowTopRightOnSquareIcon className="mr-2 h-4 w-4" />
        Open target
      </Button>
    );
  }

  return <CopyButton label="Admin request" value={adminRequestText} />;
}

function canOpenReadinessTarget(
  uiTarget: string | null | undefined,
  permissions: { canManageMcpPolicy: boolean; canOpenWorkspaceTarget: boolean },
) {
  if (!uiTarget) return false;
  if (permissions.canManageMcpPolicy) return true;
  return permissions.canOpenWorkspaceTarget && uiTarget.startsWith('/workspaces/');
}

function buildSetupSteps(
  readiness: SemanticContextMCPReadinessResponse | undefined,
  checks: SemanticContextMCPReadinessCheck[],
): SetupStep[] {
  const tenantCheck = findCheck(checks, ['tenant_enabled', 'tenant_config']);
  const workspacePolicyCheck = findCheck(checks, ['workspace_allowed']);
  const workspaceOptInCheck = findCheck(checks, ['workspace_opt_in']);
  const activePackCheck = findCheck(checks, ['active_context_pack']);
  const toolPolicyChecks = checks.filter((check) => check.key.endsWith('_allowed') && check.key !== 'workspace_allowed');
  const blockedToolCheck = toolPolicyChecks.find((check) => check.status === 'blocked');
  const toolPolicyStatus = blockedToolCheck
    ? 'blocked'
    : toolPolicyChecks.length > 0
      ? 'passed'
      : 'not_executed';

  return [
    {
      key: 'tenant-policy',
      title: 'Admin policy enabled',
      description: tenantCheck?.message ?? 'Tenant-level MCP control-plane policy must be enabled.',
      status: tenantCheck?.status ?? (readiness?.tenant_enabled ? 'passed' : 'blocked'),
      check: tenantCheck,
    },
    {
      key: 'workspace-allowlist',
      title: 'Workspace allowed',
      description: workspacePolicyCheck?.message ?? 'Admin policy must allow this workspace.',
      status: workspacePolicyCheck?.status ?? (readiness?.workspace_allowed ? 'passed' : 'blocked'),
      check: workspacePolicyCheck,
    },
    {
      key: 'tool-allowlist',
      title: 'Tools allowed',
      description: blockedToolCheck?.message ?? (toolPolicyChecks.length > 0
        ? 'Admin tool allowlist checks passed for the required workspace tools.'
        : 'Admin policy must allow the tools this workspace should expose.'),
      status: toolPolicyStatus,
      check: blockedToolCheck ?? toolPolicyChecks[0],
    },
    {
      key: 'workspace-exposure',
      title: 'Workspace exposure on',
      description: workspaceOptInCheck?.message ?? 'The workspace owner must opt this workspace into external MCP access.',
      status: workspaceOptInCheck?.status ?? (readiness?.workspace_enabled ? 'passed' : 'blocked'),
      check: workspaceOptInCheck,
    },
    {
      key: 'runtime-context',
      title: 'Runtime context active',
      description: activePackCheck?.message ?? 'Structured tools need an active Runtime Context Pack.',
      status: activePackCheck?.status ?? (readiness?.active_context_pack ? 'passed' : 'not_executed'),
      check: activePackCheck,
    },
    {
      key: 'client-oauth',
      title: 'Client connected',
      description: formatSmokeMessage(readiness?.smoke.external_chatgpt_auth.message ?? 'OAuth must be completed by a real MCP client.'),
      status: readiness?.smoke.external_chatgpt_auth.status ?? 'not_executed',
    },
    {
      key: 'live-tool-call',
      title: 'Tool call verified',
      description: readiness?.smoke.real_sql_execution.message ?? 'Run a live tool call from the external client.',
      status: readiness?.smoke.real_sql_execution.status ?? 'not_executed',
    },
  ];
}

function findCheck(checks: SemanticContextMCPReadinessCheck[], keys: string[]) {
  return keys.map((key) => checks.find((check) => check.key === key)).find(Boolean);
}

function formatWorkspaceScope(scope: SemanticContextMCPReadinessResponse['allowed_workspace_scope']) {
  if (scope === 'all_current_and_future') return 'Workspace policy: All current and future workspaces';
  if (scope === 'explicit') return 'Workspace policy: Explicit workspace allowlist';
  if (scope === 'not_configured') return 'Workspace policy: Not configured';
  return 'Workspace policy: Disabled';
}

function formatToolScope(scope: SemanticContextMCPReadinessResponse['allowed_tool_scope']) {
  if (scope === 'all_current_and_future') return 'Tool policy: All current and future tools';
  if (scope === 'explicit') return 'Tool policy: Explicit tool allowlist';
  if (scope === 'not_configured') return 'Tool policy: Not configured';
  return 'Tool policy: Disabled';
}

function CheckIcon({ status }: { status: SemanticContextMCPReadinessCheck['status'] }) {
  const className = 'mt-0.5 h-5 w-5 flex-shrink-0';
  if (status === 'passed') {
    return <CheckCircleIcon className={cn(className, 'text-green-600 dark:text-green-400')} />;
  }
  if (status === 'blocked') {
    return <XCircleIcon className={cn(className, 'text-amber-600 dark:text-amber-400')} />;
  }
  return <InformationCircleIcon className={cn(className, 'text-gray-400 dark:text-gray-500')} />;
}

function ConnectionDetailsPanel({
  oauth,
  activePackLabel,
  redirectUriCount,
  smoke,
}: {
  oauth?: SemanticContextMCPReadinessResponse['oauth'] | SemanticContextMCPPolicyResponse['oauth'];
  activePackLabel: string;
  redirectUriCount: number;
  smoke?: SemanticContextMCPReadinessResponse['smoke'];
}) {
  const rows: Array<[string, string]> = oauth
    ? [
        ['MCP Server URL', oauth.server_url],
        ['Authorization metadata', oauth.oauth_authorization_server_metadata_url],
        ['Protected resource metadata', oauth.oauth_protected_resource_metadata_url],
        ['OAuth Client ID', oauth.oauth_client_id ?? ''],
      ].filter(([, value]) => Boolean(value)) as Array<[string, string]>
    : [];

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2/40">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-medium text-charcoal dark:text-white">Connect a client</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Use the tab for the client you are configuring. Client OAuth and live tool calls remain manual until a real client records them.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant={activePackLabel === 'No active pack' ? 'warning' : 'success'}>{activePackLabel}</Badge>
          <Badge variant={redirectUriCount > 0 ? 'success' : 'warning'}>
            {redirectUriCount} redirect URI{redirectUriCount === 1 ? '' : 's'}
          </Badge>
        </div>
      </div>

      <Tabs defaultValue="chatgpt">
        <TabsList variant="pills" className="flex flex-wrap">
          <TabsTrigger value="chatgpt" variant="pills">ChatGPT</TabsTrigger>
          <TabsTrigger value="codex" variant="pills">Codex</TabsTrigger>
          <TabsTrigger value="claude" variant="pills">Claude</TabsTrigger>
          <TabsTrigger value="generic" variant="pills">Generic MCP</TabsTrigger>
        </TabsList>

        <TabsContent value="chatgpt">
          <ClientConnectionGuide
            title="ChatGPT connector"
            steps={[
              'Create or edit a ChatGPT connector that supports remote MCP with OAuth.',
              'Use the MCP server URL and authorization metadata from this workspace.',
              'Paste the callback URL supplied by ChatGPT into the admin MCP policy redirect URI list.',
              'Complete OAuth in ChatGPT, then run list_workspaces and a workspace tool call.',
            ]}
            rows={rows}
            redirectUris={oauth?.oauth_redirect_uris ?? []}
          />
        </TabsContent>

        <TabsContent value="codex">
          <ClientConnectionGuide
            title="Codex MCP client"
            steps={[
              'Add this server as a remote MCP endpoint in the Codex client configuration.',
              'Use OAuth metadata discovery when available; otherwise copy the server URL and metadata URLs.',
              'Authorize the browser-based OAuth flow with a user who can access this workspace.',
              'Verify list_workspaces, select_workspace, and the target workspace tool from Codex.',
            ]}
            rows={rows}
            redirectUris={oauth?.oauth_redirect_uris ?? []}
          />
        </TabsContent>

        <TabsContent value="claude">
          <ClientConnectionGuide
            title="Claude connector"
            steps={[
              'Create a Claude connector or integration that supports remote MCP with OAuth.',
              'Use the MCP server URL and OAuth metadata values below.',
              'Paste the callback URL supplied by Claude into the admin MCP policy redirect URI list.',
              'Complete OAuth in Claude, then verify the published tools are visible.',
            ]}
            rows={rows}
            redirectUris={oauth?.oauth_redirect_uris ?? []}
          />
        </TabsContent>

        <TabsContent value="generic">
          <ClientConnectionGuide
            title="Generic MCP client"
            steps={[
              'Use the shared tenant MCP endpoint for the server URL.',
              'Prefer OAuth protected resource and authorization server metadata discovery.',
              'Use the public OAuth client ID if your client asks for one.',
              'Run list_workspaces first, then select_workspace and a workspace-specific tool.',
            ]}
            rows={rows}
            redirectUris={oauth?.oauth_redirect_uris ?? []}
          />
        </TabsContent>
      </Tabs>

      {smoke && (
        <div className="mt-4 grid gap-2 md:grid-cols-2">
          <SmokeMarker label="list_workspaces visibility" marker={smoke.list_workspaces_visibility} />
          <SmokeMarker label="structured_query exposed" marker={smoke.structured_query_exposed} />
          <SmokeMarker label="External client OAuth" marker={smoke.external_chatgpt_auth} />
          <SmokeMarker label="Real SQL execution" marker={smoke.real_sql_execution} />
        </div>
      )}
    </div>
  );
}

function ClientConnectionGuide({
  title,
  steps,
  rows,
  redirectUris,
}: {
  title: string;
  steps: string[];
  rows: Array<[string, string]>;
  redirectUris: string[];
}) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
        <p className="text-sm font-semibold text-charcoal dark:text-white">{title}</p>
        <ol className="mt-3 space-y-2">
          {steps.map((step, index) => (
            <li key={step} className="flex gap-3 text-xs leading-5 text-gray-600 dark:text-gray-300">
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-semibold text-gray-600 dark:bg-dark-surface-2 dark:text-gray-300">
                {index + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {rows.map(([label, value]) => (
          <ConnectionDetail key={label} label={label} value={value} />
        ))}
        {rows.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">Connection metadata has not loaded.</p>
        )}
      </div>

      {redirectUris.length > 0 ? (
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Registered redirect URIs
          </p>
          <div className="space-y-2">
            {redirectUris.map((uri) => (
              <ConnectionDetail key={uri} label="Redirect URI" value={uri} />
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-200">
          No redirect URIs are registered yet. Add the callback URL supplied by the client in Admin Settings before testing OAuth.
        </div>
      )}
    </div>
  );
}

function ConnectionDetail({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-dark-border dark:bg-dark-surface">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</p>
        <CopyButton label={label} value={value} />
      </div>
      <code className="block overflow-x-auto whitespace-nowrap text-xs text-charcoal dark:text-gray-100">{value}</code>
    </div>
  );
}

function SmokeMarker({ label, marker }: { label: string; marker: SemanticContextMCPReadinessResponse['smoke']['external_chatgpt_auth'] }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-dark-border dark:bg-dark-surface">
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-charcoal dark:text-white">{label}</p>
        <Badge variant={marker.status === 'passed' ? 'success' : marker.status === 'blocked' ? 'warning' : 'secondary'}>
          {formatStatus(marker.status)}
        </Badge>
      </div>
      <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">{formatSmokeMessage(marker.message)}</p>
    </div>
  );
}

function PublishedToolsPanel({
  tools,
  structuredSupported,
}: {
  tools: SemanticContextMCPToolExposure[];
  structuredSupported: boolean;
}) {
  const groups = groupTools(tools, structuredSupported);

  return (
    <div>
      <div className="mb-3">
        <p className="text-sm font-medium text-charcoal dark:text-white">Published tools</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Effective tool exposure for this workspace, grouped by what the external client can use.
        </p>
      </div>
      <div className="space-y-4">
        <ToolGroup
          title="Available in this workspace"
          description="These tools are currently published for this workspace and user."
          tools={groups.available}
          structuredSupported={structuredSupported}
          emptyText="No tools are available yet."
        />
        <ToolGroup
          title="Blocked by admin policy"
          description="Allow these tools in Admin Settings before they appear to external clients."
          tools={groups.policyBlocked}
          structuredSupported={structuredSupported}
          emptyText="No tools are blocked by admin policy."
        />
        <ToolGroup
          title="Blocked by workspace capability"
          description="These tools need workspace data, permissions, or an active Runtime Context Pack."
          tools={groups.capabilityBlocked}
          structuredSupported={structuredSupported}
          emptyText="No tools are blocked by workspace capability."
        />
        {tools.length === 0 && (
          <div className="flex items-start gap-4 rounded-lg border border-gray-200 bg-gray-50/50 p-4 dark:border-dark-border dark:bg-dark-surface-2/30">
            <div className="mt-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-400 dark:bg-gray-800">
              <ShieldCheckIcon className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-charcoal dark:text-white">MCP tool manifest</h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Tool readiness is loading from the backend MCP control plane.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ToolGroup({
  title,
  description,
  tools,
  structuredSupported,
  emptyText,
}: {
  title: string;
  description: string;
  tools: SemanticContextMCPToolExposure[];
  structuredSupported: boolean;
  emptyText: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2/40">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-medium text-charcoal dark:text-white">{title}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
        </div>
        <Badge variant={tools.length > 0 ? 'outline' : 'secondary'}>{tools.length} tools</Badge>
      </div>
      <div className="space-y-2">
        {tools.map((tool) => (
          <ToolRow key={tool.name} tool={tool} structuredSupported={structuredSupported} />
        ))}
        {tools.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-200 bg-white p-3 text-xs text-gray-500 dark:border-dark-border dark:bg-dark-surface dark:text-gray-400">
            {emptyText}
          </div>
        )}
      </div>
    </div>
  );
}

function ToolRow({
  tool,
  structuredSupported,
}: {
  tool: SemanticContextMCPToolExposure;
  structuredSupported: boolean;
}) {
  const supported = tool.name === 'structured_query' ? structuredSupported : tool.workspace_capability_supported !== false;
  const waitingOnPack = Boolean(tool.requires_active_context_pack && !tool.active_context_pack_available);
  const statusLabel = !supported ? 'Unsupported' : tool.enabled ? 'Published' : waitingOnPack ? 'Needs active pack' : 'Blocked';

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4 dark:border-dark-border dark:bg-dark-surface-2/30">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-charcoal dark:text-white">{tool.name}</h3>
            <Badge variant={tool.enabled ? 'success' : waitingOnPack ? 'warning' : 'secondary'}>{statusLabel}</Badge>
            {tool.code && (
              <code className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600 dark:bg-dark-surface dark:text-gray-300">
                {tool.code}
              </code>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{tool.title}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge variant="outline" className="text-xs">{tool.required_permission}</Badge>
            {tool.requires_active_context_pack && <Badge variant="warning" className="text-xs">Requires active pack</Badge>}
            {tool.required_workspace_capability && <Badge variant="outline" className="text-xs">{tool.required_workspace_capability}</Badge>}
          </div>
          {tool.disabled_reason && (
            <div className="mt-2 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
              <LockClosedIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              <span>{tool.disabled_reason}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function groupTools(tools: SemanticContextMCPToolExposure[], structuredSupported: boolean) {
  return tools.reduce<{
    available: SemanticContextMCPToolExposure[];
    policyBlocked: SemanticContextMCPToolExposure[];
    capabilityBlocked: SemanticContextMCPToolExposure[];
  }>(
    (acc, tool) => {
      const supported = tool.name === 'structured_query'
        ? structuredSupported
        : tool.workspace_capability_supported !== false;
      const policyBlocked = tool.code === 'tool_not_allowed';

      if (tool.enabled) {
        acc.available.push(tool);
      } else if (policyBlocked) {
        acc.policyBlocked.push(tool);
      } else if (!supported || tool.requires_active_context_pack || tool.disabled_reason) {
        acc.capabilityBlocked.push(tool);
      } else {
        acc.policyBlocked.push(tool);
      }
      return acc;
    },
    { available: [], policyBlocked: [], capabilityBlocked: [] },
  );
}

function CopyButton({ label, value }: { label: string; value: string }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => {
        void navigator.clipboard?.writeText(value);
      }}
    >
      <ClipboardDocumentIcon className="mr-1 h-4 w-4" />
      Copy {label}
    </Button>
  );
}

function buildAdminRequest(workspaceId: number, readiness?: SemanticContextMCPReadinessResponse): string {
  const blocker = readiness?.first_blocker;
  return [
    `Please update MCP access for workspace ${workspaceId}.`,
    blocker ? `Current blocker: ${blocker.code} - ${blocker.message}` : 'Current blocker: MCP readiness has not loaded.',
    blocker?.next_action ? `Requested action: ${blocker.next_action}` : null,
  ].filter(Boolean).join('\n');
}

function formatStatus(status: SemanticContextMCPReadinessCheck['status']): string {
  if (status === 'not_executed') return 'Manual';
  return status === 'passed' ? 'Passed' : 'Blocked';
}

function formatSmokeMessage(message: string): string {
  return message.replace(/External ChatGPT OAuth/g, 'External client OAuth');
}
